import { useState, useRef, useEffect } from 'react';
import DestinationCard from './destination-card';
import Modal from '@ui/modal/modal';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Popover from '@ui/popover/popover';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { useAppSelector } from '@/store/hooks';
import { selectUserChannel } from '@/store/slices/user/selectors';
import { selectGreenroomMeeting } from '@/store/slices/project/selectors';
import { DestinationTypeEnum } from '@/types/greenroom/destination.types';
import { DESTINATION_TYPE_OPTIONS, getDefaultDestinationOption } from './destination-type-options';
import type { DestinationTypeOption } from './destination-type-options';
import { useDestinationManager } from './use-destination-manager';

type DestinationModalProps = {
  open: boolean;
  onClose: () => void;
  initialExpandedId?: number | string | null;
};

const DestinationModal = ({ open, onClose, initialExpandedId }: DestinationModalProps) => {
  const portalEl = useRef<HTMLDivElement | null>(null);
  const listBottomRef = useRef<HTMLDivElement | null>(null);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const prevNewCountRef = useRef(0);

  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // intercept Esc in capture phase so it closes the dropdown first, not the modal
  useEffect(() => {
    if (!typeDropdownOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [typeDropdownOpen]);

  const channel = useAppSelector(selectUserChannel);
  const greenroomMeeting = useAppSelector(selectGreenroomMeeting);
  const roomId = greenroomMeeting?.meeting ? String(greenroomMeeting.meeting) : undefined;

  const {
    destinations,
    setDestinations,
    touchedUrlIds,
    setTouchedUrlIds,
    loadDestinations,
    handleSave,
    hasChanges,
    validation,
    setPendingDeleteIds,
  } = useDestinationManager(roomId, onClose, channel?.type);

  // scroll to bottom
  useEffect(() => {
    const newCount = destinations.filter((d) => d.isNew).length;
    if (newCount > prevNewCountRef.current) {
      const timer = setTimeout(() => {
        let el: HTMLElement | null = listBottomRef.current?.parentElement ?? null;
        while (el) {
          const { overflow, overflowY } = window.getComputedStyle(el);
          if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) {
            el.scrollTop = el.scrollHeight;
            break;
          }
          el = el.parentElement;
        }
      }, 200);
      prevNewCountRef.current = newCount;
      return () => clearTimeout(timer);
    }
    prevNewCountRef.current = newCount;
  }, [destinations]);

  // scroll specific dest into view
  useEffect(() => {
    if (!open || !roomId) return;
    loadDestinations(roomId).then((loaded) => {
      if (!loaded || !initialExpandedId) return;
      const targetId = String(initialExpandedId);
      if (loaded.some((d) => d.localId === targetId)) {
        setExpandedId(targetId);
        setTimeout(() => {
          const el = listContainerRef.current?.querySelector(`[data-card-id="${targetId}"]`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 200);
      }
    });
  }, [open, roomId, loadDestinations, initialExpandedId]);

  // The BLP/platform destination is server-managed (returned synthetically by the API) and
  // not user-addable. Only third-party destinations appear in the Add Destination dropdown.
  const availableTypeOptions = DESTINATION_TYPE_OPTIONS;

  const handleAddDestination = (opt: DestinationTypeOption) => {
    const localId = `new-${Date.now()}`;
    setTypeDropdownOpen(false);
    setDestinations((prev) => [
      ...prev,
      {
        localId,
        isNew: true,
        type: opt.value,
        name: opt.label,
        url: '',
        streamKey: '',
        selected: false,
      },
    ]);
    setExpandedId(localId);
  };

  const handleFieldChange = (localId: string, field: string, value: string | boolean) => {
    setDestinations((prev) => prev.map((d) => (d.localId === localId ? { ...d, [field]: value } : d)));
  };

  const handleDelete = (localId: string) => {
    const dest = destinations.find((d) => d.localId === localId);
    if (!dest || dest.type === DestinationTypeEnum.Streams) return;
    if (dest.id) setPendingDeleteIds((prev) => [...prev, dest.id as number]);
    setDestinations((prev) => prev.filter((d) => d.localId !== localId));
  };

  return (
    <Modal
      backgroundColor="var(--global-cont-1)"
      open={open}
      customSize={{ width: '640px', height: '508px' }}
      onRequestClose={onClose}
      RenderHeaderContent={() => (
        <Container design="GlobalCont1" padding="24px 40px 16px 40px" borderRadius="12px 12px 0 0">
          <Text tag="h3" design="GlobalIcoText1" className="H3">
            Destinations
          </Text>
        </Container>
      )}
      RenderFooterContent={() => (
        <Container
          design="GlobalCont1"
          borderRadius="0 0 12px 12px"
          padding="24px 40px"
          className="flex fd-row jc-space-between ai-center"
        >
          {validation.hasInvalidUrls ? (
            <Text className="L2" color="var(--primitives-mode-1-red-500)">
              Please enter a valid RTMP address and stream key
            </Text>
          ) : (
            <div />
          )}
          <Container className="flex fd-row ai-center">
            <Button
              design="LocalBttnSec"
              designSize="large"
              className="L1 padding-v-8 padding-h-16 margin-r-8"
              borderRadius="100px"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              className="flex ai-center jc-center padding-v-8 padding-h-16"
              backgroundColor={getThemeColorCSSVariable()}
              disabled={!hasChanges || !validation.isValid}
              borderRadius="100px"
              onClick={handleSave}
              designSize="large"
            >
              <Text color={getThemeColorContrastCSSVariable()} className="L1">
                Save
              </Text>
            </Button>
          </Container>
        </Container>
      )}
    >
      <Container design="GlobalCont1" className="padding-h-40 padding-t-4" height="100%">
        <Popover
          open={typeDropdownOpen}
          onClose={() => setTypeDropdownOpen(false)}
          portalEl={portalEl}
          disabled={destinations.length >= 5}
          cycleWithArrowKeys
          renderTriggerButton={() => (
            <Button
              className="clear-button width-100-percent"
              borderRadius="8px"
              design="LocalBttnSecTransp"
              outlineOnFocus
              onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
            >
              <Container
                background="var(--BG)"
                className="flex ai-center jc-center padding-h-16 padding-v-24"
                borderRadius="8px"
                width="100%"
              >
                <Container className="flex ai-center gap-8">
                  <Icon name={ICONS.ADD} size={12} color="var(--global-ico-text-3)" />
                  <Text className="S3" design="GlobalIcoText3">
                    Add Destination
                  </Text>
                </Container>
              </Container>
            </Button>
          )}
        >
          {({ triggerBounds }) => (
            <Container
              design="GlobalCont2"
              borderRadius="8px"
              className="overflow-hidden position-fixed"
              style={{
                top: (triggerBounds?.bottom ?? 0) + 4,
                left: triggerBounds?.left ?? 0,
                width: triggerBounds?.width ?? 'auto',
                zIndex: 1001,
              }}
            >
              {availableTypeOptions.map((opt) => (
                <Button
                  key={opt.value}
                  className="clear-button width-100-percent text-left padding-v-10 padding-h-16"
                  hoverBackgroundColor="var(--global-cont-3)"
                  onClick={() => handleAddDestination(opt)}
                >
                  <Container className="flex ai-center gap-12">
                    <Icon name={opt.icon} size={16} color="var(--global-ico-text-3)" />
                    <Text className="L2" design="GlobalIcoText1">
                      {opt.label}
                    </Text>
                  </Container>
                </Button>
              ))}
            </Container>
          )}
        </Popover>

        <Container className="flex fd-column gap-8 margin-t-12" ref={listContainerRef}>
          {destinations.map((dest) => {
            const isDefault = dest.type === DestinationTypeEnum.Streams;
            const typeOpt = isDefault
              ? getDefaultDestinationOption(channel?.type)
              : (DESTINATION_TYPE_OPTIONS.find((o) => o.value === dest.type) ?? DESTINATION_TYPE_OPTIONS[0]);

            return (
              <div key={dest.localId} data-card-id={dest.localId}>
                <DestinationCard
                  localId={dest.localId}
                  name={dest.name}
                  icon={typeOpt.icon}
                  url={dest.url}
                  streamKey={dest.streamKey}
                  selected={dest.selected}
                  isExpanded={expandedId === dest.localId}
                  canDelete={!isDefault && !!roomId}
                  readOnly={isDefault}
                  editableName={dest.type === DestinationTypeEnum.Other}
                  onToggleExpand={() => setExpandedId(expandedId === dest.localId ? null : dest.localId)}
                  onToggleSelected={(id, _, val) => handleFieldChange(id, 'selected', val)}
                  onFieldChange={handleFieldChange}
                  onDelete={handleDelete}
                  urlTouched={touchedUrlIds.has(dest.localId)}
                  onUrlBlur={(id) => setTouchedUrlIds((prev) => new Set([...prev, id]))}
                />
              </div>
            );
          })}
          <div ref={listBottomRef} />
        </Container>
      </Container>
    </Modal>
  );
};

export default DestinationModal;
